import {Component, ElementRef, afterRenderEffect, inject, viewChild} from '@angular/core';
import {DatePipe, NgClass} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {RouterLink} from '@angular/router';
import {ConfirmationService} from 'primeng/api';
import {TapHelperService} from './tap-helper.service';
import {ChatMarkdown} from './chat-markdown';
import {Tooltip} from 'primeng/tooltip';

/**
 * TAP Helper: a launcher icon that opens into a panel, which can go full screen. Panel and full screen
 * are one element, so the draft, scroll position and a reply in flight carry across the switch.
 */
@Component({
  selector: 'chat-window',
  imports: [NgClass, DatePipe, FormsModule, RouterLink, ChatMarkdown, Tooltip],
  templateUrl: './chat-window.html',
  styleUrl: './chat-window.scss',
  host: {
    '(document:keydown.escape)': 'onEscape($event)',
  },
})
export class ChatWindow {
  protected chat = inject(TapHelperService);
  private host = inject<ElementRef<HTMLElement>>(ElementRef);
  private confirmationService = inject(ConfirmationService);

  draft = '';
  private chain = viewChild<ElementRef<HTMLElement>>('chain');
  private input = viewChild<ElementRef<HTMLTextAreaElement>>('input');
  /** Follow new content only while the reader is at the bottom. */
  private stick = true;

  constructor() {
    afterRenderEffect(() => {
      this.chat.entries();
      if (this.chat.state() !== 'icon' && this.stick) {
        const el = this.chain()?.nativeElement;
        el?.scrollTo({top: el.scrollHeight});
      }
    });
    afterRenderEffect(() => {
      this.chat.focusTick();
      if (this.chat.state() !== 'icon') {
        this.input()?.nativeElement.focus();
        this.stick = true;
      }
    });
  }

  // Esc minimises from panel or full screen, unless something else (a dialog, an overlay) is handling it.
  onEscape(event: Event): void {
    const target = event.target as Node | null;
    if (this.chat.state() === 'icon' || event.defaultPrevented
      || (target !== document.body && !this.host.nativeElement.contains(target))) {
      return;
    }
    event.preventDefault();
    this.chat.minimise();
  }

  // Enter sends, Shift+Enter is a newline; never mid-IME composition.
  onEnter(event: Event): void {
    const e = event as KeyboardEvent;
    if (e.shiftKey || e.isComposing) {
      return;
    }
    e.preventDefault();
    this.submit();
  }

  // Minimise only when RouterLink navigates in place; modifier-clicks open elsewhere.
  onOpenDraft(event: Event): void {
    const e = event as MouseEvent;
    if (!e.ctrlKey && !e.metaKey && !e.shiftKey && !e.altKey) {
      this.chat.minimise();
    }
  }

  submit(): void {
    if (this.chat.send(this.draft)) {
      this.draft = '';
      this.stick = true;
    }
  }

  confirmHide(): void {
    this.confirmationService.confirm({
      message: '<p>The TAP Assistant button will no longer appear in the corner of the screen.</p>'
        + '<p class="mt-3">You can open TAP Assistant again from Information in the sidebar.</p>',
      header: 'Hide TAP Assistant?',
      icon: '',
      rejectLabel: 'Cancel',
      rejectButtonProps: {
        label: 'Cancel',
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: 'Hide',
      },
      accept: () => this.chat.hide(),
    });
  }

  onScroll(): void {
    const el = this.chain()!.nativeElement;
    this.stick = el.scrollHeight - el.scrollTop - el.clientHeight < 48;
  }
}
